import { OnboardingTarget } from "../onboarding/onboardingTargets";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  SelectedTab,
  selectSelectedTab,
  setSelectedTab,
} from "../../app/slices/GlobalSlice";
import { selectIsDirty } from "../../app/slices/TrainingSlice";
import {
  selectCurrentOrganization,
  selectUsername,
} from "../../app/slices/UserSlice";
import PiConfirmationDialog from "../../components/PiConfirmationDialog";
import FileExplorerDialog from "../file_explorer/FileExplorerDialog";
import useOnExitEditor from "../../hooks/OnExitEditorHook";
import PiAccountDialog from "../menu/PiAccountPopover";
import PiSwitchOrganizationDialog from "../menu/PiSwitchOrganizationDialog";
import SettingsModal from "../menu/SettingsModal";
import PublishDialog from "../publish_course/PublishDialog";
import EditorRail, {
  EditorRailButton,
  EditorRailDivider,
} from "../editor_rail/EditorRail";

export default function ScenarioBuilderTabRail() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedTab = useSelector(selectSelectedTab);
  const isDirty = useSelector(selectIsDirty);
  const username = useSelector(selectUsername);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const { onExitEditorCleanup } = useOnExitEditor();

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [confirmLeaveDialogOpen, setConfirmLeaveDialogOpen] = useState(false);
  const [isFileExplorerOpen, setIsFileExplorerOpen] = useState(false);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [accountAnchorEl, setAccountAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [isSwitchOrganizationOpen, setIsSwitchOrganizationOpen] =
    useState(false);

  const accountTooltip = currentOrganization?.name
    ? `${username} · ${currentOrganization.name}`
    : username;

  function onExitEditor() {
    onExitEditorCleanup();
    navigate("/");
  }

  function onExitClicked() {
    if (isDirty) {
      setConfirmLeaveDialogOpen(true);
    } else {
      onExitEditor();
    }
  }

  return (
    <EditorRail
      testId="scenario-builder-tab-rail"
      onboardingId={OnboardingTarget.Editor3DTabRail}
      bottom={
        <>
          <EditorRailButton
            label={t("settings") as string}
            icon={<SettingsOutlinedIcon fontSize="inherit" />}
            onClick={() => setIsSettingsOpen(true)}
            testId="scenario-tabrail-settings"
          />
          <EditorRailButton
            label={t("exitEditor") as string}
            icon={<LogoutOutlinedIcon fontSize="inherit" />}
            danger
            onClick={onExitClicked}
            testId="scenario-tabrail-exit"
          />

          <EditorRailDivider testId="scenario-tabrail-account-divider" />

          <EditorRailButton
            label={accountTooltip ?? "account"}
            icon={<AccountCircleOutlinedIcon fontSize="inherit" />}
            active={Boolean(accountAnchorEl)}
            onClick={(event) =>
              setAccountAnchorEl(event.currentTarget as HTMLButtonElement)
            }
            testId="scenario-tabrail-account"
          />
        </>
      }
    >
      <EditorRailButton
        label="Scene"
        icon={<AccountTreeOutlinedIcon fontSize="inherit" />}
        active={selectedTab === SelectedTab.Timeline}
        onClick={() => dispatch(setSelectedTab(SelectedTab.Timeline))}
        testId="scenario-tabrail-scene"
      />
      <EditorRailButton
        label="Actors"
        icon={<PeopleAltOutlinedIcon fontSize="inherit" />}
        active={selectedTab === SelectedTab.ActorPresets}
        onClick={() => dispatch(setSelectedTab(SelectedTab.ActorPresets))}
        testId="scenario-tabrail-actors"
      />
      <EditorRailButton
        label="Props"
        icon={<Inventory2OutlinedIcon fontSize="inherit" />}
        active={selectedTab === SelectedTab.PropPresets}
        onClick={() => dispatch(setSelectedTab(SelectedTab.PropPresets))}
        testId="scenario-tabrail-props"
      />
      <EditorRailButton
        label={t("project_settings.tab_label") as string}
        icon={<TuneOutlinedIcon fontSize="inherit" />}
        active={selectedTab === SelectedTab.ProjectSettings}
        onClick={() => dispatch(setSelectedTab(SelectedTab.ProjectSettings))}
        testId="scenario-tabrail-project-settings"
      />

      <EditorRailDivider testId="scenario-tabrail-divider" />


      <EditorRailButton
        label={t("fileExplorer") as string}
        icon={<FolderOutlinedIcon fontSize="inherit" />}
        onClick={() => setIsFileExplorerOpen(true)}
        testId="scenario-tabrail-file-explorer"
      />
      <EditorRailButton
        label={t("publish") as string}
        icon={<SendOutlinedIcon fontSize="inherit" />}
        onClick={() => setIsPublishOpen(true)}
        testId="scenario-tabrail-publish"
      />

      <FileExplorerDialog
        open={isFileExplorerOpen}
        onClose={() => setIsFileExplorerOpen(false)}
      />
      <PublishDialog
        open={isPublishOpen}
        onClose={() => setIsPublishOpen(false)}
      />
      <SettingsModal
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
      <PiConfirmationDialog
        open={confirmLeaveDialogOpen}
        onClose={() => setConfirmLeaveDialogOpen(false)}
        title={t("exit_editor.title")}
        text={t("exit_editor.description")}
        onConfirm={onExitEditor}
        confirmText={t("exit_editor.confirm")}
        cancelText={t("exit_editor.cancel")}
        confirmButtonVariant="piBlue"
      />
      <PiAccountDialog
        id="scenario-tabrail-account-popover"
        anchorEl={accountAnchorEl}
        open={Boolean(accountAnchorEl)}
        onClose={() => setAccountAnchorEl(null)}
        switchOrgCallback={() => setIsSwitchOrganizationOpen(true)}
        settingsCallback={() => setIsSettingsOpen(true)}
      />
      <PiSwitchOrganizationDialog
        open={isSwitchOrganizationOpen}
        onConfirm={() => setIsSwitchOrganizationOpen(false)}
      />
    </EditorRail>
  );
}
